import { useState } from 'react';
import { getUserErrorMessage } from '../utils/errorMessages';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Loader2, ArrowRight } from 'lucide-react';

const InviteAcceptPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [formData, setFormData] = useState({
    fullName: '',
    password: '',
    confirmPassword: '',
  });
  const [loading, setLoading] = useState(false);
  const { acceptInvite } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      toast.error('Wachtwoorden komen niet overeen');
      return;
    }

    if (formData.password.length < 8) {
      toast.error('Wachtwoord moet minimaal 8 tekens bevatten');
      return;
    }

    setLoading(true);

    try {
      await acceptInvite(token, formData.password, formData.fullName);
      toast.success('Account aangemaakt! Welkom bij Spooker.');
      navigate('/dashboard');
    } catch (error) {
      toast.error(getUserErrorMessage(error, 'De uitnodiging kon niet worden geaccepteerd. Neem contact op met uw organisatie.'));
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white p-8">
        <div className="w-full max-w-sm space-y-6 text-center">
          <h2 className="text-2xl font-semibold tracking-tight text-black">
            Ongeldige uitnodiging
          </h2>
          <p className="text-sm text-neutral-600">
            Deze uitnodigingslink is ongeldig of verlopen.
          </p>
          <Link to="/login">
            <Button className="w-full bg-black text-white hover:bg-neutral-800">
              Ga naar inloggen
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-white p-8">
      <div className="w-full max-w-sm space-y-8">
        <div className="space-y-2 text-center">
          <h1 className="text-2xl font-bold tracking-tight text-black">Spooker</h1>
          <h2 className="text-xl font-semibold tracking-tight text-black">
            Uitnodiging accepteren
          </h2>
          <p className="text-sm text-neutral-600">
            U bent uitgenodigd om deel te nemen aan een organisatie. Maak uw account aan om te beginnen.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="fullName" className="text-sm font-medium text-black">
                Volledige naam
              </Label>
              <Input
                id="fullName"
                name="fullName"
                type="text"
                placeholder="Jan Jansen"
                value={formData.fullName}
                onChange={handleChange}
                required
                className="border-neutral-200 focus:border-black focus:ring-black"
                data-testid="invite-fullname-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium text-black">
                Wachtwoord
              </Label>
              <Input
                id="password"
                name="password"
                type="password"
                placeholder="Minimaal 8 tekens"
                value={formData.password}
                onChange={handleChange}
                required
                minLength={8}
                className="border-neutral-200 focus:border-black focus:ring-black"
                data-testid="invite-password-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-sm font-medium text-black">
                Bevestig wachtwoord
              </Label>
              <Input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                placeholder="Herhaal uw wachtwoord"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
                className="border-neutral-200 focus:border-black focus:ring-black"
                data-testid="invite-confirm-password-input"
              />
            </div>
          </div>

          <Button
            type="submit"
            className="w-full bg-black text-white hover:bg-neutral-800"
            disabled={loading}
            data-testid="invite-submit-button"
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                Account aanmaken
                <ArrowRight className="ml-2 h-4 w-4" strokeWidth={1.5} />
              </>
            )}
          </Button>

          <p className="text-xs text-center text-neutral-500">
            Door te accepteren gaat u akkoord met onze voorwaarden en privacybeleid.
          </p>
        </form>
      </div>
    </div>
  );
};

export default InviteAcceptPage;
